import type { LucideIcon } from 'lucide-react';
import type { PermissionScope } from '@shared/types/auth';

/**
 * Navigation model.
 *
 * Every destination declares the permission that makes it reachable, so the
 * sidebar, the post-login landing redirect and the route guards all read the
 * same contract. A new backend permission needs no structural change here —
 * only the key it is gated on.
 */

export interface NavAccess {
  /** Required permission. */
  permission?: string;
  /** Alternative: any one of these grants visibility. */
  anyOf?: readonly string[];
  /**
   * Alternative: ALL of these are required — for a destination whose route
   * guard is `allOf`, so the link is offered exactly when the page will open.
   */
  allOf?: readonly string[];
  /**
   * Show only when `permission` carries one of these backend scopes.
   *
   * The scope comes from the server's own grant, so a destination can be
   * offered to a self-service user and withheld from an administrator (or the
   * reverse) without any role name appearing in the navigation. Ignored when
   * the check cannot resolve scopes.
   */
  scopes?: readonly PermissionScope[];
  /** Show only when `permission` does NOT carry one of these scopes. */
  notScopes?: readonly PermissionScope[];
}

export interface NavChildItem extends NavAccess {
  label: string;
  to: string;
  icon: LucideIcon;
  /** Optional sub-section label shown above this child. */
  heading?: string;
  /** Active only on this exact path (an Overview whose path prefixes its siblings). */
  end?: boolean;
  /**
   * Extra URL prefixes that also mark this link active — e.g. Vendors stays
   * highlighted on the Services and Expenses screens of the same business area.
   */
  activePrefixes?: readonly string[];
}

/**
 * A collapsible sub-group inside a group (Finance → Asset Management).
 *
 * It expands only while one of its own screens is open, so the sidebar shows
 * at most one nested list at a time. It has no page of its own: its header
 * opens the first child the user may reach.
 */
export interface NavSubGroupItem {
  type: 'subgroup';
  label: string;
  icon: LucideIcon;
  /** URL prefixes that make the sub-group active (and therefore expanded). */
  matchPrefixes: readonly string[];
  children: NavChildItem[];
}

export type NavGroupChild = NavChildItem | NavSubGroupItem;

export function isNavSubGroup(child: NavGroupChild): child is NavSubGroupItem {
  return 'type' in child && child.type === 'subgroup';
}

export interface NavLinkItem extends NavAccess {
  type: 'link';
  label: string;
  to: string;
  icon: LucideIcon;
}

export interface NavGroupItem {
  type: 'group';
  label: string;
  icon: LucideIcon;
  /** Persisted expand/collapse state key. */
  storageKey: string;
  /** URL prefix(es) used to mark the group active. */
  matchPrefix: string | readonly string[];
  children: NavGroupChild[];
}

export type NavItem = NavLinkItem | NavGroupItem;

export type PermissionCheck = {
  can: (permission: string) => boolean;
  canAny: (permissions: readonly string[]) => boolean;
  /**
   * Optional so existing callers that only pass `can`/`canAny` keep working;
   * without it a scope-conditioned entry is simply treated as unconditional.
   */
  hasScope?: (permission: string, scopes: readonly PermissionScope[]) => boolean;
};

/** Is a single destination reachable for the current user? */
export function isNavItemVisible(
  item: NavAccess,
  { can, canAny, hasScope }: PermissionCheck,
): boolean {
  if (item.anyOf && item.anyOf.length > 0) return canAny(item.anyOf);
  if (item.allOf && item.allOf.length > 0) return item.allOf.every((key) => can(key));
  if (!item.permission) {
    // No declared permission — always visible (e.g. Settings).
    return true;
  }
  if (!can(item.permission)) return false;

  // Scope conditions refine a permission the user already holds; they never
  // grant one. Skipped entirely when the caller cannot resolve scopes.
  if (hasScope) {
    if (item.scopes && !hasScope(item.permission, item.scopes)) return false;
    if (item.notScopes && hasScope(item.permission, item.notScopes)) return false;
  }
  return true;
}

/**
 * Filter the nav tree down to what the user may reach. Groups whose children
 * are all hidden are dropped entirely rather than rendering an empty section.
 */
export function visibleNavItems<T extends NavItem>(
  items: readonly T[],
  check: PermissionCheck,
): T[] {
  return items.flatMap<T>((item) => {
    if (item.type === 'link') return isNavItemVisible(item, check) ? [item] : [];
    const children = visibleChildren(item.children, check);
    return children.length > 0 ? [{ ...item, children }] : [];
  });
}

/**
 * Filter one group's children. A sub-group with no visible child is dropped.
 * A sub-section heading belongs to its section, not to one link: when the
 * child carrying it is hidden, it moves to the next visible child.
 */
function visibleChildren(items: readonly NavGroupChild[], check: PermissionCheck): NavGroupChild[] {
  const result: NavGroupChild[] = [];
  let heading: string | undefined;
  for (const child of items) {
    if (isNavSubGroup(child)) {
      const nested = child.children.filter((entry) => isNavItemVisible(entry, check));
      if (nested.length > 0) result.push({ ...child, children: nested });
      continue;
    }
    heading = child.heading ?? heading;
    if (!isNavItemVisible(child, check)) continue;
    result.push({ ...child, heading });
    heading = undefined;
  }
  return result;
}

/** The path a group child opens: a link's own, or a sub-group's first link. */
export function navChildPath(child: NavGroupChild): string | undefined {
  return isNavSubGroup(child) ? child.children[0]?.to : child.to;
}

/** Does the current path fall under any of these prefixes? */
export function matchesPrefix(pathname: string, prefixes: string | readonly string[]): boolean {
  const list = typeof prefixes === 'string' ? [prefixes] : prefixes;
  return list.some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`));
}

/**
 * First destination the user can actually open, in sidebar order. Used by the
 * root redirect so a user without `dashboard.view` lands somewhere useful
 * instead of on an Access Denied page. Returns null when nothing is reachable.
 */
export function firstAccessiblePath(
  sections: ReadonlyArray<{ items: readonly NavItem[] }>,
  check: PermissionCheck,
): string | null {
  for (const section of sections) {
    for (const item of visibleNavItems(section.items, check)) {
      if (item.type === 'link') return item.to;
      const firstChild = item.children[0];
      const path = firstChild ? navChildPath(firstChild) : undefined;
      if (path) return path;
    }
  }
  return null;
}
